/**
 * In-memory global search for the mock API client (tracker item 61). Same
 * rules as the backend where the UI depends on them: 2-character minimum
 * (400), limit 1-20 (default 5), role-gated types (technicians get
 * customers, properties and jobs; other non-office roles 403), soft-deleted
 * properties left out, phone numbers compared digits-only, job / payment
 * id prefixes, one group per searched type. Callers pass the signed-in
 * tenant's records only.
 */
import * as types from './types';

const OFFICE_TYPES: types.GlobalSearchEntityType[] = ['customer', 'property', 'job', 'invoice', 'payment', 'estimate'];
const TYPES_BY_ROLE: Record<string, types.GlobalSearchEntityType[]> = {
  ADMIN: OFFICE_TYPES,
  OFFICE_MANAGER: OFFICE_TYPES,
  TECHNICIAN: ['customer', 'property', 'job'],
};
const LABELS: Record<types.GlobalSearchEntityType, string> = {
  customer: 'Customers', property: 'Properties', job: 'Jobs', invoice: 'Invoices', payment: 'Payments', estimate: 'Estimates',
};

export interface MockSearchRecords {
  customers: types.Customer[];
  properties: types.Property[];
  jobs: types.Job[];
  invoices: types.Invoice[];
  payments: types.Payment[];
  estimates: types.EstimateSummary[];
}

export class MockSearchError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/** Searchable types for a role, or null when the route would refuse it (403). */
export function mockSearchTypes(role: string): types.GlobalSearchEntityType[] | null {
  return TYPES_BY_ROLE[role] ?? null;
}

const join = (parts: Array<string | null | undefined>, sep = ' · ') => parts.map((p) => (p ?? '').trim()).filter(Boolean).join(sep);
const humanize = (v: string) => { const w = v.toLowerCase().replace(/_/g, ' '); return w.charAt(0).toUpperCase() + w.slice(1); };

export function mockGlobalSearch(
  role: string,
  rawQuery: string,
  options: types.GlobalSearchOptions,
  records: MockSearchRecords
): types.GlobalSearchResponse {
  const query = (rawQuery ?? '').trim().replace(/\s+/g, ' ');
  if (query.length < types.GLOBAL_SEARCH_MIN_LENGTH) throw new MockSearchError(400, 'Search query must be at least 2 characters');
  const limit = options.limit ?? 5;
  if (!Number.isInteger(limit) || limit < 1 || limit > 20) throw new MockSearchError(400, 'limit must be an integer from 1 to 20');
  const permitted = mockSearchTypes(role);
  if (!permitted) throw new MockSearchError(403, 'Insufficient permissions');
  const searched = permitted.filter((t) => !options.types || options.types.includes(t));

  const q = query.toLowerCase();
  const digits = /^[\d\s()+.-]+$/.test(query) ? query.replace(/\D/g, '') : '';
  const idLike = /^[0-9a-f-]{4,}$/.test(q);
  const has = (v: string | null | undefined) => !!v && v.toLowerCase().includes(q);
  const phone = (v: string | null | undefined) => digits.length >= 3 && !!v && v.replace(/\D/g, '').includes(digits);
  const customerById = new Map(records.customers.map((c) => [c.id, c]));
  const customerHit = (id: string) => { const c = customerById.get(id); return !!c && (has(c.name) || has(c.email) || phone(c.phone)); };
  const liveProperty = (p: types.Property | undefined) => !!p && !(p as { deletedAt?: string | null }).deletedAt;
  const propertyById = new Map(records.properties.map((p) => [p.id, p]));
  const addressHit = (p: types.Property | undefined) => liveProperty(p) && has(join([p!.address, p!.city, p!.state, p!.zip], ' '));

  const build: Record<types.GlobalSearchEntityType, () => types.GlobalSearchResult[]> = {
    customer: () => records.customers
      .filter((c) => has(c.name) || has(c.email) || phone(c.phone) || has(join([c.address, c.city, c.state, c.zip], ' ')))
      .map((c) => ({ type: 'customer', id: c.id, title: c.name, subtitle: join([c.email, c.phone, join([c.city, c.state], ', ')]), customerId: null, path: `/customers/${c.id}` })),
    property: () => records.properties
      .filter((p) => liveProperty(p) && (addressHit(p) || has(customerById.get(p.customerId)?.name)))
      .map((p) => ({ type: 'property', id: p.id, title: p.address, subtitle: join([`${p.city}, ${p.state} ${p.zip}`, customerById.get(p.customerId)?.name]), customerId: p.customerId, path: `/customers/${p.customerId}/properties/${p.id}` })),
    job: () => records.jobs
      .filter((j) => (idLike && j.id.toLowerCase().startsWith(q)) || customerHit(j.customerId) || addressHit(propertyById.get(j.propertyId)))
      .map((j) => ({ type: 'job', id: j.id, title: `Job #${j.id.slice(0, 8)}`, subtitle: join([j.customer?.name, join([j.property?.address, j.property?.city], ', '), humanize(j.status), j.scheduledDate ?? 'Unscheduled']), customerId: j.customerId, path: `/jobs/${j.id}` })),
    invoice: () => records.invoices
      .filter((i) => has(i.invoiceNumber) || customerHit(i.billingCustomerId))
      .map((i) => ({ type: 'invoice', id: i.id, title: `Invoice ${i.invoiceNumber}`, subtitle: join([i.billingCustomer?.name, humanize(i.status), i.invoiceDate]), customerId: i.billingCustomerId, path: `/invoices/${i.id}` })),
    payment: () => records.payments
      .filter((p) => has(p.referenceNumber) || (idLike && p.id.toLowerCase().startsWith(q)) || customerHit(p.billingCustomerId))
      .map((p) => ({ type: 'payment', id: p.id, title: `Payment ${p.id.slice(0, 8)}`, subtitle: join([p.billingCustomer?.name, `$${p.amount}`, p.paymentDate, p.referenceNumber ? `Ref ${p.referenceNumber}` : null]), customerId: p.billingCustomerId, path: `/payments/${p.id}` })),
    estimate: () => records.estimates
      .filter((e) => has(e.estimateNumber) || has(e.title) || customerHit(e.customer.id) || addressHit(propertyById.get(e.property.id)))
      .map((e) => ({ type: 'estimate', id: e.id, title: e.title ? `Estimate ${e.estimateNumber}: ${e.title}` : `Estimate ${e.estimateNumber}`, subtitle: join([e.customer.name, e.property.address, humanize(e.status)]), customerId: e.customer.id, path: `/estimates/${e.id}` })),
  };

  return {
    query,
    limit,
    types: searched,
    groups: searched.map((type) => ({ type, label: LABELS[type], results: build[type]().slice(0, limit) })),
  };
}
